import React, {useEffect} from 'react';
import DispatchListContent from "./detailed/dispatch-list-content.jsx";
import EncounterListContent from "./detailed/encounter-list-content.jsx";
import StatusContent from "./detailed/status-content.jsx";
import CardsContent from "./detailed/cards-content.jsx";
import BosListContent from "./detailed/bos-list-content.jsx";
import ReportUtils from "../../../../../utils/report-utils.js";
import GraphicUtils from "../../../../../utils/graphic-utils.js";
import {translate} from "../../../../../components/translate/translate.jsx";
import BarChartContent from "./detailed/bar-chart-content.jsx";
import DetentionListContent from "./detailed/detention-list-content.jsx";
import ProjectUtils from "../../../../../utils/project-utils.js";
import ApprehensionListContent from "./detailed/apprehension-list-content.jsx";
import {safeFormatDateTime, safeJsonJoin, safeJsonTranslateJoin} from "./utils/safe-operations.jsx";
import {navigateHook} from "../../../../../hooks/use-navigate.js";

const DetailedContent = ({ data, boName, month, year, collapsed }) => {
    const navigate = navigateHook();

    useEffect(() => {

        // EXPORT DISPATCH ========================
        if (data?.dispatch_cards?.[0]?.total > 0) {
            ReportUtils.createExportableTable(
                ["Descrição", "Quantidade"],

                [
                    ["Total de despachos", data.dispatch_cards[0].total ?? 0],
                    [`${boName} com despacho`, data.dispatch_cards[0].dispatch_bo ?? 0],
                    ["Tempo médio de despachos", data.dispatch_avg_time?.[0]?.avg ?? 0]
                ],

                "Cards (Despachos)"
            );

            const dispatchStatus = GraphicUtils.horizontalStackedBarChart(data?.dispatch_status, "dispatch-status", "dispatch_status");

            if (dispatchStatus) {
                ReportUtils.createExportableTable(
                    ["Status", "Quantidade"],

                    dispatchStatus.labels.map((label, index) => [
                        label,
                        dispatchStatus.values[index]
                    ]),

                    "Status dos despachos"
                );
            }

            ReportUtils.createExportableTable(
                ['ID', 'Talão', `${boName}`, 'Natureza', 'D/H inicial', 'D/H final'],

                (data?.dispatch_list ?? []).map(element => [
                    element.dispatch_id ?? "",
                    element.checkbook ?? "",
                    element.bo ?? "",
                    element.dispatch_nature ?? "",
                    safeFormatDateTime(element.dispatch_start) ?? "",
                    safeFormatDateTime(element.dispatch_end) ?? ""
                ]),

                'Lista de despachos'
            );
        }

        // EXPORT ENCOUNTER ========================
        if (data?.encounter_cards?.[0]?.total > 0) {
            ReportUtils.createExportableTable(
                ["Descrição", "Quantidade"],

                [
                    ["Total de abordagens", data.encounter_cards[0].total ?? 0],
                    [`${boName} com abordagem`, data.encounter_cards[0].encounter_bo ?? 0],
                    ["Indivíduos abordados", data.encounter_cards[0].individuals ?? 0],
                    ["Veículos abordados", data.encounter_cards[0].vehicles ?? 0]
                ],

                "Cards (Abordagens)"
            );

            const encounterStatus = GraphicUtils.horizontalStackedBarChart(data?.encounter_status, "encounter-status", "encounter_status");

            if (encounterStatus) {
                ReportUtils.createExportableTable(
                    ["Status", "Quantidade"],

                    encounterStatus.labels.map((label, index) => [
                        label,
                        encounterStatus.values[index]
                    ]),

                    "Status das abordagens"
                );
            }

            ReportUtils.createExportableTable(
                ['ID', `${boName}`, 'D/H inicial', 'Status', 'Abordado por', 'Justificativa', 'Indivíduos', 'Veículos'],

                (data?.encounter_list ?? []).map(element => [
                    element.id ?? "",
                    element.bo ?? "",
                    safeFormatDateTime(element.dt_start) ?? "",
                    element.status ?? "",
                    element.namewar ?? "",
                    element.obs ?? "",
                    element.individuals ?? 0,
                    element.vehicles ?? 0
                ]),

                'Lista de abordagens'
            );
        }

        // EXPORT BO =============================
        if (data?.bos_cards?.[0]?.total_bos > 0) {
            ReportUtils.createExportableTable(
                ["Descrição", "Quantidade"],

                [
                    [`Total de ${boName}`, data.bos_cards[0].total_bos ?? 0],
                    [`${boName} com flagrante`, data.bos_cards[0].bo_flagrant ?? 0]
                ],

                `Cards (${boName})`
            );

            const bosStatus = (data?.bos_status ?? []).map(element => ({
                ...element,
                status: translate(element.status)
            }));

            ReportUtils.createExportableTable(
                ["Status", "Quantidade"],

                bosStatus.map(element => [
                    element.status,
                    element.qty
                ]),

                `Status de ${boName}`
            );

            ReportUtils.createExportableTable(
                [`${boName}`, 'Status', 'D/H do fato', 'Cadastrado por', 'Aprovado por', 'Flagrante', 'Naturezas'],

                (data?.bos_list ?? []).map(element => [
                    element.id ?? "",
                    translate(element.status) ?? "",
                    element.dt_fact ?? "",
                    ReportUtils.formatText(element.register) ?? "",
                    ReportUtils.formatText(element.approve) ?? "",
                    translate(element.flagrant) ?? "",
                    element.reason ?? ""
                ]),

                `Lista de ${boName}`
            );
        }

        // EXPORT DETENTION ============================
        if (data?.detention_cards?.[0]?.detention > 0) {
            ReportUtils.createExportableTable(
                ["Descrição", "Quantidade"],

                [
                    ["Total de detenções", data.detention_cards[0].detention ?? 0],
                    ["Indivíduos presos", data.detention_cards[0].ind_arrested ?? 0],
                    ["Indivíduos apreendidos", data.detention_cards[0].ind_seized ?? 0]
                ],

                "Cards (Detenções)"
            );

            ReportUtils.createExportableTable(
                ["Descrição", "Quantidade"],

                (data?.detention_referral ?? []).map(element => [
                    element.referral ?? "",
                    element.qty ?? 0
                ]),

                "Encaminhamentos"
            );

            ReportUtils.createExportableTable(
                [boName, "D/H do fato", "CPF", "Nome", "Sexo", "Idade", "Condições", "Encaminhamentos"],

                (data?.detention_list ?? []).map(element => [
                    element.bo_gcm ?? "",
                    safeFormatDateTime(element.dt_fact) ?? "",
                    ProjectUtils.formatCpfToView(element.cpf) ?? "Não informado",
                    element.name ?? "Não informado",
                    translate(element.sex) ?? "",
                    element.age_range ?? "",
                    safeJsonJoin(element.conditions) ?? "",
                    safeJsonTranslateJoin(element.referrals) ?? ""
                ]),

                "Lista de detenções"
            );
        }

        // EXPORT APPREHENSION =====================================
        if (data?.apprehension_cards?.[0]?.total_apprehension > 0) {
            ReportUtils.createExportableTable(
                ["Descrição", "Quantidade"],

                [
                    ["Total de apreensões", data.apprehension_cards[0].total_apprehension ?? 0],
                    ["Apreensões em flagrante", data.apprehension_cards[0].apprehension_flagrant ?? 0]
                ],

                "Cards (Apreensões)"
            );

            ReportUtils.createExportableTable(
                ["Tipo de apreensão", "Quantidade"],

                (data?.complete_apprehension_type ?? [])
                    .slice(0, 10)
                    .map(element => [
                        element.type ?? "",
                        element.qty ?? 0
                ]),

                "Apreensões por tipo"
            );

            ReportUtils.createExportableTable(
                [boName, "D/H do fato", "Tipo de apreensão", "Quantidade"],

                (data?.apprehension_list ?? []).map(element => [
                    element.id ?? "",
                    element.dt_fact ?? "",
                    element.type ?? "",
                    element.qty ?? 0
                ]),

                "Lista de apreensões"
            );
        }

    }, [data, boName]);

    return (
        <>

            { data?.dispatch_cards?.[0].total > 0 ? (
                <>
                    <div id="dispatch-content">
                        <div className="report-divisor mb-6">
                            <span className="divisor-title flex flex-row items-center justify-center">
                                <div>DESPACHOS <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                                <a onClick={() => navigate('/reports/dispatch_x_ray', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Raio X de despachos</a>
                            </span>
                        </div>

                        <CardsContent
                            key={`dispatch-cards-${collapsed}`}
                            cards={[
                                {
                                    title: "Total de despachos",
                                    value: data?.dispatch_cards?.[0].total ?? 0
                                },
                                {
                                    title: `${boName} com despacho`,
                                    value: data?.dispatch_cards?.[0].dispatch_bo ?? 0
                                },
                                {
                                    title: "Tempo médio de despachos",
                                    exportValue: data?.dispatch_avg_time?.[0].avg ?? 0,
                                    value: data?.dispatch_avg_time?.[0].avg?.includes("±")? (
                                        <>
                                            { data?.dispatch_avg_time?.[0].avg.split("±")[0].trim()}
                                            <span className="text-base ml-1 text-[20px]">± { data?.dispatch_avg_time?.[0].avg.split("±")[1].trim()}</span>
                                        </>
                                    ) : data?.dispatch_avg_time?.[0]?.avg ?? 0
                                }
                            ]}
                        />

                        <StatusContent
                            key={`dispatch-status-${collapsed}`}
                            data={data}
                            dataTable="dispatch_status"
                            elementId="dispatch-status"
                            elementLabel="dispatch_status"
                            title="Status dos despachos"
                        />

                        <DispatchListContent key={`dispatch-list-${collapsed}`} data={data} boName={boName}/>
                    </div>
                </>
            ) : (
                <div id="dispatch-content" className={'print:!break-inside-avoid'}>
                    <div className="report-divisor my-3">
                        <span className="divisor-title flex flex-row items-center justify-center">
                            <div>DESPACHOS <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                            <a onClick={() => navigate('/reports/dispatch_x_ray', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Raio X de despachos</a>
                        </span>
                    </div>

                    <div id="noDataMessage" className={"alert alert-info p-2 h6 text-center mb-10"}>Nenhum registro encontrado.</div>
                </div>
            )}

            { data?.encounter_cards?.[0].total > 0 ? (
                <>
                    <div id="encounter-content" className={'print:!break-inside-avoid'}>
                        <div className="report-divisor mb-6">
                            <span className="divisor-title flex flex-row items-center justify-center">
                                <div>ABORDAGENS <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                                <a onClick={() => navigate('/reports/encounter_x_ray', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Raio X de abordagens</a>
                            </span>
                        </div>

                        <CardsContent
                            key={`encounter-cards-${collapsed}`}
                            cards={[
                                {
                                    title: "Total de abordagens",
                                    value: data?.encounter_cards?.[0].total ?? 0
                                },
                                {
                                    title: `${boName} com abordagem`,
                                    value: data?.encounter_cards?.[0].encounter_bo ?? 0
                                },
                                {
                                    title: "Indivíduos abordados",
                                    value: data?.encounter_cards?.[0].individuals ?? 0
                                },
                                {
                                    title: "Veículos abordados",
                                    value: data?.encounter_cards?.[0].vehicles ?? 0
                                }
                            ]}
                            columns={4}
                        />

                        <StatusContent
                            key={`encounter-status-${collapsed}`}
                            data={data}
                            dataTable="encounter_status"
                            elementId="encounter-status"
                            elementLabel="encounter_status"
                            title="Status das abordagens"
                        />

                        <EncounterListContent key={`encounter-list-${collapsed}`} data={data} boName={boName}/>
                    </div>
                </>
            ) : (
                <div id="encounter-content" className={'print:!break-inside-avoid'}>
                    <div className="report-divisor my-3">
                        <span className="divisor-title flex flex-row items-center justify-center">
                            <div>ABORDAGENS <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                            <a onClick={() => navigate('/reports/encounter_x_ray', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Raio X de abordagens</a>
                        </span>
                    </div>

                    <div id="noDataMessage" className={"alert alert-info p-2 h6 text-center mb-10"}>Nenhum registro encontrado.</div>
                </div>
            )}

            { data?.bos_cards?.[0].total_bos > 0 ? (
                <>
                    <div id="bo-content" className={'print:!break-inside-avoid'}>
                        <div className="report-divisor mb-6">
                            <span className="divisor-title flex flex-row items-center justify-center">
                                <div>{boName.toUpperCase()} <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                                <a onClick={() => navigate('/reports/bo_x_ray', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Raio X de {boName}</a>
                            </span>
                        </div>

                        <CardsContent
                            key={`bos-cards-${collapsed}`}
                            cards={[
                                {
                                    title: `Total de ${boName}`,
                                    idTitle: `Apenas ${boName} com status aprovado`,
                                    value: data?.bos_cards?.[0].total_bos ?? 0
                                },
                                {
                                    title: `${boName} com flagrante`,
                                    idTitle: `Apenas ${boName} com status aprovado`,
                                    value: data?.bos_cards?.[0].bo_flagrant ?? 0
                                }
                            ]}
                            columns={2}
                        />

                        <BarChartContent key={`bos-status-${collapsed}`} data={data?.bos_status} elementId={'bos-status'} elementLabels={'status'} title={`Status de ${boName}`} translateLabels={true}/>

                        <BosListContent key={`bos-list-${collapsed}`} data={data} boName={boName}/>
                    </div>
                </>
            ) : (
                <div id="bo-content" className={'print:!break-inside-avoid'}>
                    <div className="report-divisor my-3">
                        <span className="divisor-title flex flex-row items-center justify-center">
                            <div>{boName.toUpperCase()} <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                            <a onClick={() => navigate('/reports/bo_x_ray', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Raio X de {boName}</a>
                        </span>
                    </div>

                    <div id="noDataMessage" className={"alert alert-info p-2 h6 text-center mb-10"}>Nenhum registro encontrado.</div>
                </div>
            )}

            { data?.detention_cards?.[0].detention > 0 ? (
                <>
                    <div id="detention-content" className={'print:!break-inside-avoid'}>
                        <div className="report-divisor mb-6">
                            <span className="divisor-title">
                                DETENÇÕES <span className={'font-bold'}>{month.toUpperCase()} {year}</span>
                            </span>
                        </div>

                        <CardsContent
                            key={`detention-cards-${collapsed}`}
                            cards={[
                                {
                                    title: "Total de detenções",
                                    idTitle: "Detenções = Presos + Apreendidos",
                                    value: data?.detention_cards?.[0].detention ?? 0
                                },
                                {
                                    title: "Indivíduos presos",
                                    value: data?.detention_cards?.[0].ind_arrested ?? 0
                                },
                                {
                                    title: "Indivíduos apreendidos",
                                    value: data?.detention_cards?.[0].ind_seized ?? 0
                                },
                            ]}
                        />

                        <BarChartContent key={`detention-status-${collapsed}`} data={data?.detention_referral} elementId={'detention-referral'} elementLabels={'referral'} title={"Encaminhamentos"}/>

                        <DetentionListContent key={`detention-list-${collapsed}`} data={data} boName={boName}/>
                    </div>
                </>
            ) : (
                <div id="detention-content" className={'print:!break-inside-avoid'}>
                    <div className="report-divisor my-3">
                            <span className="divisor-title">
                                DETENÇÕES <span className={'font-bold'}>{month.toUpperCase()} {year}</span>
                            </span>
                    </div>

                    <div id="noDataMessage" className={"alert alert-info p-2 h6 text-center mb-10"}>Nenhum registro encontrado.</div>
                </div>
            )}


            { data?.apprehension_cards?.[0].total_apprehension > 0 ? (
                <>
                    <div id="apprehensions-content" className={'print:!break-inside-avoid'}>
                        <div className="report-divisor mb-6">
                            <span className="divisor-title flex flex-row items-center justify-center">
                                <div>APREENSÕES <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                                <a onClick={() => navigate('/reports/bo_apprehensions', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Apreensões</a>
                            </span>
                        </div>

                        <CardsContent
                            key={`apprehension-cards-${collapsed}`}
                            cards={[
                                {
                                    title: "Total de apreensões",
                                    value: data?.apprehension_cards?.[0].total_apprehension ?? 0
                                },
                                {
                                    title: "Apreensões em flagrante",
                                    value: data?.apprehension_cards?.[0].apprehension_flagrant ?? 0
                                }
                            ]}
                            columns={2}
                        />

                        <BarChartContent key={`apprehension-status-${collapsed}`} data={data?.complete_apprehension_type} elementId={'apprehensions-type'} elementLabels={'type'} title={"Apreensões por tipo"} top={10}/>

                        <ApprehensionListContent key={`apprehension-list-${collapsed}`} data={data} boName={boName}/>
                    </div>
                </>
            ) : (
                <div id="detention-apprehensions" className={'print:!break-inside-avoid'}>
                    <div className="report-divisor my-3">
                            <span className="divisor-title flex flex-row items-center justify-center">
                                <div>APREENSÕES <span className={'font-bold'}>{month.toUpperCase()} {year}</span></div>
                                <a onClick={() => navigate('/reports/bo_apprehensions', true)} className={'absolute right-12 italic text-[12.5px] text-zinc-600 cursor-pointer hover:text-[#1588F3]'}>Acessar Apreensões</a>
                            </span>
                    </div>

                    <div id="noDataMessage" className={"alert alert-info p-2 h6 text-center mb-10"}>Nenhum registro encontrado.</div>
                </div>
            )}

        </>
    );
};

export default DetailedContent;
